import { withNamespaces } from "react-i18next";
import { Badge } from "reactstrap";

const normalizeObligations = (obligations, obligation) => {
    if (Array.isArray(obligations)) {
        return obligations.filter(Boolean);
    }

    if (obligation) {
        return [obligation];
    }

    return [];
};

const ObligationItem = ({ obligation, t, showDivider }) => {
    const {
        gap_reason: gapReason,
        required_document: requiredDocument,
        blocking = false
    } = obligation;

    const isBlocking = blocking === true;

    return (
        <div
            className={showDivider ? "pt-2 mt-2 border-top" : ""}
            style={
                showDivider
                    ? { borderColor: "rgba(0, 0, 0, 0.06)" }
                    : undefined
            }
        >
            <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="flex-grow-1 min-w-0">
                    {gapReason && (
                        <div className="small mb-0 text-body">
                            {gapReason}
                        </div>
                    )}

                    {requiredDocument && (
                        <div className="small text-muted mt-1 mb-0">
                            <span className="fw-medium">
                                {t("Required document")}
                                {": "}
                            </span>
                            {requiredDocument}
                        </div>
                    )}
                </div>

                {isBlocking && (
                    <Badge
                        color="danger"
                        className="bg-danger flex-shrink-0 align-self-start"
                        pill
                    >
                        {t("Critical Requirement")}
                    </Badge>
                )}
            </div>
        </div>
    );
};

/**
 * Renders EU AI Act compliance gap notices for a questionnaire question.
 * Presentation only — no lookup, fetch, or business logic.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.obligations]
 * @param {Object} [props.obligation] Legacy single-obligation prop
 * @param {Function} props.t
 */
const ComplianceGapNotice = ({ obligations, obligation, t }) => {
    const items = normalizeObligations(obligations, obligation);

    if (items.length === 0) {
        return null;
    }

    return (
        <div
            className="compliance-gap-notice mt-2 mb-1 rounded px-3 py-2"
            style={{
                backgroundColor: "rgba(255, 193, 7, 0.08)",
                border: "1px solid rgba(255, 193, 7, 0.28)"
            }}
            role="status"
        >
            <div className="d-flex align-items-start gap-2">
                <i
                    className="ri-error-warning-line text-warning mt-1 flex-shrink-0"
                    aria-hidden="true"
                />

                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium small text-dark">
                        {t("Action Required")}
                    </div>

                    <div className="small text-muted mt-1 mb-0">
                        {t("The following compliance requirements are currently not satisfied.")}
                    </div>

                    <div className="mt-2">
                        {items.map((item, index) => (
                            <ObligationItem
                                key={item.code || `obligation-${index}`}
                                obligation={item}
                                t={t}
                                showDivider={index > 0}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withNamespaces()(ComplianceGapNotice);
